import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, PlusCircle } from "lucide-react";
import { CommitteeDraft } from "@/components/governance/CommitteeForm";
import { CommitteeDetailsModal } from "@/components/governance/CommitteeDetailsModal";
import { CommitteeSheetForm } from "@/components/governance/CommitteeSheetForm";
import { CommitteeTree } from "@/components/governance/CommitteeTree";
import { GovernanceGraphView } from "@/components/governance/GovernanceGraphView";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGovernance } from "@/hooks/useGovernance";

interface FrameworkWizardProps {
  frameworkId?: string | null;
  parentId?: string | null;
  onPublish?: () => void;
  onSaveDraft?: () => void;
  onCancel?: () => void;
}

export function FrameworkWizard({ frameworkId: initialFrameworkId, parentId: initialParentId, onPublish, onSaveDraft, onCancel }: FrameworkWizardProps) {
  const {
    members,
    committees,
    addFramework,
    addCommittee,
    updateCommittee,
    updateFramework,
    updateFrameworkStatus,
    getFrameworkById,
    getCommitteeById,
  } = useGovernance();

  const [frameworkId, setFrameworkId] = useState<string | null>(initialFrameworkId ?? null);
  const [frameworkName, setFrameworkName] = useState("Governance Framework");
  const [frameworkDescription, setFrameworkDescription] = useState("New governance hierarchy setup.");
  const [selectedCommitteeId, setSelectedCommitteeId] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetParentId, setSheetParentId] = useState<string | null>(initialParentId ?? null);
  const [editingCommitteeId, setEditingCommitteeId] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  useEffect(() => {
    if (initialFrameworkId) setFrameworkId(initialFrameworkId);
    if (initialParentId) setSheetParentId(initialParentId);
  }, [initialFrameworkId, initialParentId]);

  const activeFramework = useMemo(
    () => (frameworkId ? getFrameworkById(frameworkId) : undefined),
    [frameworkId, getFrameworkById],
  );

  const scopedCommittees = committees.filter((committee) => committee.frameworkId === activeFramework?.id);
  const selectedCommittee = selectedCommitteeId ? getCommitteeById(selectedCommitteeId) : undefined;
  const selectedCommitteeOwner = selectedCommittee?.ownerId
    ? members.find((member) => member.id === selectedCommittee.ownerId)
    : undefined;
  const selectedCommitteeChair = selectedCommittee?.chairpersonId
    ? members.find((member) => member.id === selectedCommittee.chairpersonId)
    : undefined;
  const sheetParentCommittee = sheetParentId ? getCommitteeById(sheetParentId) : undefined;
  const editingCommittee = editingCommitteeId ? getCommitteeById(editingCommitteeId) : undefined;

  useEffect(() => {
    if (!activeFramework) return;
    setFrameworkName(activeFramework.name);
    setFrameworkDescription(activeFramework.description);
  }, [activeFramework?.id]);

  const createFrameworkIfNeeded = () => {
    if (frameworkId) return frameworkId;
    const framework = addFramework({
      name: frameworkName,
      description: frameworkDescription,
      chairpersonId: null,
      createdBy: members[0]?.id ?? "system",
    });
    setFrameworkId(framework.id);
    return framework.id;
  };

  const saveFrameworkDetails = () => {
    const id = createFrameworkIfNeeded();
    updateFramework(id, {
      name: frameworkName.trim() || "Governance Framework",
      description: frameworkDescription.trim() || "New governance hierarchy setup.",
    });
    return id;
  };

  const openAddCommittee = (parentId: string | null) => {
    setSheetParentId(parentId);
    setEditingCommitteeId(null);
    setSheetOpen(true);
  };

  const openEditCommittee = (committeeId: string) => {
    setEditingCommitteeId(committeeId);
    const committee = getCommitteeById(committeeId);
    setSheetParentId(committee?.parentId ?? null);
    setSheetOpen(true);
  };

  const handleSaveCommittee = (draft: CommitteeDraft) => {
    if (editingCommittee) {
      updateCommittee(editingCommittee.id, {
        name: draft.name.trim() || editingCommittee.name,
        description: draft.description,
        ownerId: draft.ownerId,
        type: draft.type,
        memberIds: draft.memberIds,
      });
      return;
    }
    const id = saveFrameworkDetails();
    const parent = sheetParentId ? getCommitteeById(sheetParentId) : undefined;
    addCommittee({
      frameworkId: id,
      name: draft.name.trim() || "New Committee",
      description: draft.description,
      level: parent ? parent.level + 1 : 1,
      parentId: parent?.id ?? null,
      ownerId: draft.ownerId,
      type: draft.type,
      memberIds: draft.memberIds,
    });
  };

  const handleSaveAsDraft = () => {
    const id = saveFrameworkDetails();
    updateFrameworkStatus(id, "draft");
    onSaveDraft?.();
  };

  const missingOwners = scopedCommittees.filter((committee) => !committee.ownerId);
  const hasLevelOne = scopedCommittees.some((committee) => committee.level === 1);
  const canPublish = scopedCommittees.length > 0 && hasLevelOne && missingOwners.length === 0;
  const publishDisabledReason = !hasLevelOne
    ? "Add at least one level 1 committee to publish."
    : missingOwners.length > 0
      ? "Assign committee owners before publishing."
      : scopedCommittees.length === 0
        ? "Add at least one committee to publish."
        : "";

  return (
    <div className="space-y-5">
      <div className="sticky top-0 z-10 -mx-1 rounded-lg border bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-medium text-foreground">Create New Governance Framework</h1>
            <p className="text-muted-foreground">
              Build your governance hierarchy in one view and publish when validation is clear.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Badge variant={canPublish ? "secondary" : "outline"}>
              {canPublish ? "Ready to publish" : `${missingOwners.length} warning(s)`}
            </Badge>
            {onCancel ? (
              <Button variant="outline" onClick={onCancel}>
                Done
              </Button>
            ) : null}
            <Button variant="outline" onClick={handleSaveAsDraft}>
              Save as Draft
            </Button>
            <Button
              disabled={!canPublish}
              title={publishDisabledReason || undefined}
              onClick={() => {
                const id = saveFrameworkDetails();
                updateFrameworkStatus(id, "deployed");
                onPublish?.();
              }}
            >
              Publish
            </Button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Framework Setup</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="framework-name">Framework Name</Label>
              <Input
                id="framework-name"
                value={frameworkName}
                onChange={(event) => setFrameworkName(event.target.value)}
                onBlur={saveFrameworkDetails}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="framework-description">Description</Label>
              <Input
                id="framework-description"
                value={frameworkDescription}
                onChange={(event) => setFrameworkDescription(event.target.value)}
                onBlur={saveFrameworkDetails}
              />
            </div>

            <div className="border-t pt-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-medium text-foreground">Committee Tree</h3>
                <span className="text-xs text-muted-foreground">{scopedCommittees.length} committee(s)</span>
              </div>

              <div className="max-h-[420px] overflow-y-auto pr-1">
                {scopedCommittees.length > 0 ? (
                  <CommitteeTree
                    committees={scopedCommittees}
                    members={members}
                    selectedCommitteeId={selectedCommitteeId}
                    onSelect={setSelectedCommitteeId}
                    onAddSubCommittee={(committee) => openAddCommittee(committee.id)}
                    onEditCommittee={(committee) => openEditCommittee(committee.id)}
                  />
                ) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    No committees yet. Add your first Level 1 committee to begin the hierarchy.
                  </div>
                )}
              </div>

              <Button
                variant="outline"
                className="mt-3 w-full"
                onClick={() => openAddCommittee(null)}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Level 1 Committee
              </Button>
            </div>

            <div className="space-y-2 border-t pt-4">
              <p className="text-sm font-medium text-foreground">Validation Status</p>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2">
                  {hasLevelOne ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <span>{hasLevelOne ? "Level 1 committee exists" : "At least one Level 1 committee required"}</span>
                </div>
                <div className="flex items-center gap-2">
                  {missingOwners.length === 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <span>
                    {missingOwners.length === 0
                      ? "All committees have owners"
                      : `${missingOwners.length} committee(s) are missing owners`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {scopedCommittees.length > 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-amber-600" />
                  )}
                  <span>
                    {scopedCommittees.length > 0
                      ? "Hierarchy has at least one committee"
                      : "Add at least one committee to continue"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Live Hierarchy Graph</CardTitle>
              {selectedCommittee ? (
                <Button size="sm" variant="outline" onClick={() => openAddCommittee(selectedCommittee.id)}>
                  <PlusCircle className="mr-2 h-4 w-4" />
                  Add Committee Under {selectedCommittee.name}
                </Button>
              ) : null}
            </div>
          </CardHeader>
          <CardContent>
            <GovernanceGraphView
              committees={scopedCommittees}
              selectedCommitteeId={selectedCommitteeId}
              onCommitteeSelect={(committeeId) => {
                setSelectedCommitteeId(committeeId);
                setDetailsModalOpen(true);
              }}
            />
          </CardContent>
        </Card>
      </div>

      <CommitteeSheetForm
        open={sheetOpen}
        onOpenChange={(open) => {
          setSheetOpen(open);
          if (!open) setEditingCommitteeId(null);
        }}
        parentCommittee={sheetParentCommittee}
        committeeToEdit={editingCommittee}
        members={members}
        onSave={handleSaveCommittee}
      />

      <CommitteeDetailsModal
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
        committee={selectedCommittee}
        members={members}
        owner={selectedCommitteeOwner}
        chairperson={selectedCommitteeChair}
      />
    </div>
  );
}
